<template>
  <div>
    <headerSection></headerSection>

    <div class="history-page">
      <!-- Hero Section -->
      <div class="hero-section">
        <!-- Floating Balls -->
        <div class="floating-ball ball-1"></div>
        <div class="floating-ball ball-2"></div>
        <div class="floating-ball ball-3"></div>

        <div class="container">
          <div class="hero-content">
            <!-- Icon -->
            <div class="hero-icon-wrapper">
              <span class="hero-icon">📜</span>
            </div>

            <!-- Title -->
            <h1 class="title-glow">{{ $t('history_page.title') }}</h1>

            <!-- Description -->
            <p class="hero-description">{{ $t('history_page.hero_description') }}</p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container history-content">
        <!-- Stats Summary -->
        <div class="stats-summary" v-if="history.length > 0 && !loading">
          <div class="summary-item">
            <span class="summary-icon">📊</span>
            <span class="summary-value">{{ history.length }}</span>
            <span class="summary-label">{{ $t('history_page.summary_label') }}</span>
          </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="loading">
          <div class="loading-spinner"></div>
          <div class="loading-text">{{ $t('loading') }}</div>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-else-if="history.length === 0">
          <div class="empty-icon">📭</div>
          <div class="empty-text">{{ $t('history_page.no_data') }}</div>
          <p class="empty-subtext">{{ $t('history_page.empty_subtext') }}</p>
        </div>

        <!-- Transaction List -->
        <div class="transaction-list" v-else>
          <div
            v-for="(item, index) in history"
            :key="index"
            :class="['transaction-card', getTypeClass(item.type)]"
          >
            <div class="transaction-icon">
              <span>{{ getTypeIcon(item.type) }}</span>
            </div>

            <div class="transaction-details">
              <div class="transaction-type">
                <span class="type-badge" :class="getTypeClass(item.type)">
                  {{ item.type }}
                </span>
              </div>
              <div class="transaction-date">{{ item.createdateShow }}</div>
            </div>

            <div class="transaction-amount">
              <div :class="['amount-value', getAmountClass(item.type)]">
                {{ getAmountPrefix(item.type) }}{{ numberWithCommas(item.qty) }}
              </div>
            </div>

            <div class="transaction-status">
              <span :class="['status-badge', getStatusClass(item.status)]">
                <span class="status-icon">{{ getStatusIcon(item.status) }}</span>
                <span class="status-text">{{ getDisplayStatus(item.status) }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footerSection></footerSection>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/database'
import router from './../router'
import moment from 'moment-timezone'
import _ from 'lodash'

export default {
  name: 'History',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  computed: {
    user() {
      return this.$store.state.user.user
    }
  },
  data() {
    return {
      history: [],
      loading: true
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    numberWithCommas(x) {
      if (!x) x = 0
      return Number(x).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getDisplayStatus(status) {
      if (status === 'ดำเนินการแล้ว') return this.$t('history_page.status_success')
      if (status === 'ยกเลิก') return this.$t('history_page.status_cancelled')
      return this.$t('history_page.status_pending')
    },
    getTypeIcon(type) {
      const depositLabel = this.$t('history_page.type_deposit')
      const withdrawLabel = this.$t('history_page.type_withdraw')
      const wheelLabel = this.$t('history_page.type_wheel')
      const rouletteLabel = this.$t('history_page.type_roulette')
      const continueLabel = this.$t('history_page.type_continue')
      const referralLabel = this.$t('history_page.type_referral')
      const cashbackLabel = this.$t('history_page.type_cashback')

      const icons = {
        [depositLabel]: '📥',
        [withdrawLabel]: '📤',
        [wheelLabel]: '🎡',
        [rouletteLabel]: '🎰',
        [continueLabel]: '🔥',
        [referralLabel]: '👥',
        [cashbackLabel]: '💎',
        // Legacy support for Thai
        'ฝาก': '📥',
        'ถอน': '📤',
        'วงล้อ': '🎡',
        'รูเล็ต': '🎰',
        'ฝากต่อเนื่อง': '🔥',
        'พันธมิตร': '👥',
        'คืนยอดเล่น': '💎'
      }
      return icons[type] || '💰'
    },
    getTypeClass(type) {
      const depositLabel = this.$t('history_page.type_deposit')
      const withdrawLabel = this.$t('history_page.type_withdraw')
      const wheelLabel = this.$t('history_page.type_wheel')
      const rouletteLabel = this.$t('history_page.type_roulette')
      const continueLabel = this.$t('history_page.type_continue')
      const referralLabel = this.$t('history_page.type_referral')
      const cashbackLabel = this.$t('history_page.type_cashback')

      const classes = {
        [depositLabel]: 'deposit',
        [withdrawLabel]: 'withdraw',
        [wheelLabel]: 'bonus',
        [rouletteLabel]: 'bonus',
        [continueLabel]: 'bonus',
        [referralLabel]: 'bonus',
        [cashbackLabel]: 'bonus',
        // Legacy support for Thai
        'ฝาก': 'deposit',
        'ถอน': 'withdraw',
        'วงล้อ': 'bonus',
        'รูเล็ต': 'bonus',
        'ฝากต่อเนื่อง': 'bonus',
        'พันธมิตร': 'bonus',
        'คืนยอดเล่น': 'bonus'
      }
      return classes[type] || 'default'
    },
    getAmountClass(type) {
      const withdrawLabel = this.$t('history_page.type_withdraw')
      if (type === 'ถอน' || type === withdrawLabel) return 'negative'
      return 'positive'
    },
    getAmountPrefix(type) {
      const withdrawLabel = this.$t('history_page.type_withdraw')
      if (type === 'ถอน' || type === withdrawLabel) return '-'
      return '+'
    },
    getStatusIcon(status) {
      if (status === 'ดำเนินการแล้ว') return '✅'
      if (status === 'ยกเลิก') return '❌'
      return '⏳'
    },
    getStatusClass(status) {
      if (status === 'ดำเนินการแล้ว') return 'success'
      if (status === 'ยกเลิก') return 'cancelled'
      return 'pending'
    },
    getUserId() {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        return _user
      } else {
        firebase.auth().signOut()
        localStorage.removeItem('userData')
        router.replace('/login')
      }
    }
  },
  async mounted() {
    this.scrollToTop()
    this.loading = true
    this.$store.commit('user/SET_LOADING', true)

    const user = this.getUserId()
    const history = []
    let lastdate = ''

    try {
    await firebase.firestore().collection('history').where('userkey', '==', user.key).orderBy('createdate', 'desc').limit(500).get().then((snapshot) => {
      snapshot.forEach((doc) => {
        if (doc.data().qty) {
          const data = doc.data()
          data.key = doc.id
          data.createdateShow = moment(data.createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm')
          lastdate = data.createdate
          history.push(data)
        }
      })
    })

    await firebase.firestore().collection('spin').where('userkey', '==', user.key).where('createdate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
            type: (doc.data().game) ? doc.data().game : 'วงล้อ',
          createdate: doc.data().createdate,
          createdateShow: moment(doc.data().createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().value,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('roulet').where('userkey', '==', user.key).where('createdate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'รูเล็ต',
          createdate: doc.data().createdate,
          createdateShow: moment(doc.data().createdate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().value,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('continuous_deposit_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'ฝากต่อเนื่อง',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('partner_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'พันธมิตร',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    await firebase.firestore().collection('cashback_all').where('userkey', '==', user.key).where('status', '==', true).where('receivedate', '>=', lastdate).get().then((result) => {
      result.forEach((doc) => {
        history.push({
          key: doc.id,
          type: 'คืนยอดเล่น',
          createdate: doc.data().receivedate,
          createdateShow: moment(doc.data().receivedate, 'YYYY-MM-DD HH:mm:ss').format('DD MMM,YY HH:mm'),
          qty: doc.data().total,
          status: 'ดำเนินการแล้ว'
        })
      })
    })

    this.history = _.orderBy(history, ['createdate'], ['desc'])
    } catch (error) {
      console.error('Error loading history:', error)
    } finally {
      this.loading = false
    this.$store.commit('user/SET_LOADING', false)
    }
  }
}
</script>

<style scoped>
/* ===== Page Base ===== */
.history-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #2b2727 0%, #1b1b1b 50%, #2b2727 100%);
  padding-bottom: 100px;
}

/* ===== Hero Section ===== */
.hero-section {
  position: relative;
  padding: 80px 0 30px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(247, 178, 66, 0.1) 0%, transparent 100%);
}

.hero-content {
  position: relative;
  z-index: 0;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-icon-wrapper {
  display: inline-block;
  margin-bottom: 20px;
  animation: bounce 2s ease-in-out infinite;
}

.hero-icon {
  font-size: 64px;
  display: block;
  filter: drop-shadow(0 4px 15px rgba(247, 178, 66, 0.4));
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.title-glow {
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
  text-shadow:
    0 0 20px rgba(247, 178, 66, 0.5),
    0 0 40px rgba(247, 178, 66, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.5);
}

.hero-description {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

/* ===== Floating Balls ===== */
.floating-ball {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
  pointer-events: none;
  z-index: 1;
}

.floating-ball.ball-1 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #f7b242, #ce840c);
  top: -50px;
  left: -100px;
  animation: float1 6s ease-in-out infinite;
}

.floating-ball.ball-2 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #ce840c, #f7b242);
  top: 50%;
  right: -75px;
  animation: float2 8s ease-in-out infinite;
}

.floating-ball.ball-3 {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #2b2727, #f7b242);
  bottom: 20%;
  left: 10%;
  animation: float3 7s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(30px, 20px) rotate(180deg); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(-20px, 30px) rotate(-180deg); }
}

@keyframes float3 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(20px, -20px) rotate(180deg); }
}

/* ===== History Content ===== */
.history-content {
  padding: 40px 20px;
}

/* ===== Stats Summary ===== */
.stats-summary {
  margin-bottom: 30px;
  text-align: center;
}

.summary-item {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(247, 178, 66, 0.2);
  border-radius: 12px;
  padding: 20px 40px;
  display: inline-flex;
  align-items: center;
  gap: 15px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(247, 178, 66, 0.1);
}

.summary-icon {
  font-size: 32px;
}

.summary-value {
  font-size: 36px;
  font-weight: 700;
  color: #f7b242;
  text-shadow: 0 0 20px rgba(247, 178, 66, 0.5);
}

.summary-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

/* ===== Loading State ===== */
.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(247, 178, 66, 0.2);
  border-top-color: #f7b242;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
}

/* ===== Empty State ===== */
.empty-state {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(247, 178, 66, 0.2);
  border-radius: 12px;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-text {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 10px;
}

.empty-subtext {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

/* ===== Transaction List ===== */
.transaction-list {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(247, 178, 66, 0.2);
  border-radius: 12px;
  overflow: hidden;
}

.transaction-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid rgba(247, 178, 66, 0.1);
  transition: all 0.3s ease;
  gap: 15px;
}

.transaction-card:last-child {
  border-bottom: none;
}

.transaction-card:hover {
  background: rgba(247, 178, 66, 0.05);
}

.transaction-card.deposit {
  border-left: 4px solid #4facfe;
}

.transaction-card.withdraw {
  border-left: 4px solid #fa709a;
}

.transaction-card.bonus {
  border-left: 4px solid #f7b242;
}

.transaction-icon {
  font-size: 32px;
  min-width: 50px;
  text-align: center;
}

.transaction-details {
  flex: 1;
  min-width: 0;
}

.transaction-type {
  margin-bottom: 5px;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.type-badge.deposit {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
}

.type-badge.withdraw {
  background: linear-gradient(135deg, #fa709a, #fee140);
  color: #fff;
}

.type-badge.bonus {
  background: linear-gradient(135deg, #f7b242, #ce840c);
  color: #fff;
}

.transaction-date {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.transaction-amount {
  text-align: right;
  min-width: 120px;
}

.amount-value {
  font-size: 18px;
  font-weight: 700;
}

.amount-value.positive {
  color: #4ade80;
}

.amount-value.negative {
  color: #f87171;
}

.transaction-status {
  min-width: 130px;
  text-align: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.status-badge.success {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
}

.status-badge.pending {
  background: linear-gradient(135deg, #f7b242, #ce840c);
  color: #fff;
}

.status-badge.cancelled {
  background: linear-gradient(135deg, #fa709a, #fee140);
  color: #fff;
}

.status-icon {
  font-size: 14px;
}

/* ===== Responsive ===== */
@media screen and (max-width: 768px) {
  .hero-section {
    padding: 75px 0 25px;
  }

  .title-glow {
    font-size: 28px;
  }

  .hero-icon {
    font-size: 48px;
  }

  .floating-ball.ball-1 {
    width: 150px;
    height: 150px;
  }

  .floating-ball.ball-2 {
    width: 100px;
    height: 100px;
  }

  .floating-ball.ball-3 {
    width: 80px;
    height: 80px;
  }

  .transaction-card {
    flex-wrap: wrap;
    padding: 15px;
  }

  .transaction-icon {
    font-size: 24px;
    min-width: 40px;
  }

  .transaction-amount {
    min-width: 100px;
  }

  .amount-value {
    font-size: 16px;
  }

  .transaction-status {
    width: 100%;
    margin-top: 10px;
    text-align: left;
  }

  .summary-item {
    padding: 15px 30px;
  }

  .summary-value {
    font-size: 28px;
  }
}

@media screen and (max-width: 480px) {
  .hero-section {
    padding: 70px 0 20px;
  }

  .title-glow {
    font-size: 24px;
  }

  .hero-description {
    font-size: 14px;
  }

  .hero-icon {
    font-size: 40px;
  }

  .transaction-card {
    padding: 12px;
  }

  .transaction-icon {
    font-size: 20px;
    min-width: 35px;
  }

  .type-badge {
    font-size: 11px;
    padding: 3px 10px;
  }

  .transaction-date {
    font-size: 12px;
  }

  .amount-value {
    font-size: 14px;
  }

  .status-badge {
    font-size: 11px;
    padding: 5px 10px;
  }

  .summary-item {
    padding: 12px 20px;
    gap: 10px;
  }

  .summary-icon {
    font-size: 24px;
  }

  .summary-value {
    font-size: 24px;
  }

  .summary-label {
    font-size: 12px;
  }

  .empty-icon {
    font-size: 60px;
  }

  .empty-text {
    font-size: 20px;
  }

  .empty-subtext {
    font-size: 13px;
  }
}
</style>
